import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Row } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { addModal, setIsLoading as setIsLoadingApp } from "../../../../app.logic";
import { createFieldAttachment, createFieldInput, createFieldSelect } from "../../../../components/input/template-builder/template-builder.logic";
import { setAppPageHeader } from "../../../../components/layout/app-layout/app-layout.logic";
import { createColumnAttachment, createColumnInput } from "../../../../components/table/c-table.logic";
import i18n from "../../../../i18n";
import store from "../../../../store";
import { createButtonCreate, createButtonDelete, createButtonEdit, createButtonSave, createButtonView } from "../../../../utils/button";
import { modalStyleIFrame } from "../../../../utils/constant";
import { checkAttribute, initUseForm, onClickCreate, onClickDelete, onClickEdit, onClickView } from "../../../../utils/form";
import { createLoading } from "../../../../utils/loading";
import { has } from "../../../../utils/menu-context";
import {
    createAppPageHeaderTop,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionView
} from "../../../../utils/page";
import { required, validateError } from "../../../../utils/validate";

const CIFrame = defineAsyncComponent(() => import("../../../../components/iframe/CIFrame"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/master/country";
export const routerPrefix = "country-requests";

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form });
export const callSaveAll = ({ form }) => store.dispatch(`${actionPrefix}/saveAll`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });
export const callCountry = ({ searchQuery, id = null }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "refParent.id", type: "UUID", operator: "=", value: null },
        { openBucket: true, name: "isSpecial", type: "Boolean", operator: "=", value: false },
        { logic: "OR", closeBucket: true, name: "id", type: "UUID", operator: "=", value: id }
    ];
    if (searchQuery) {
        where.push({ name: "nameEn", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/master/country/search", {
            criteria: {
                select: ["**"],
                page: 0,
                size: 1000000,
                where,
                sort: [],
                children: [{ name: "children", select: ["**"], page: 0, size: 1000000, sort: [] }]
            }
        })
        .then(page => page.data);
};

export const onSearch = ({ page, isSet = true }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({
        criteria: {
            ...page,
            data: [],
            where: [
                ...where,
                { name: "isSpecial", type: "Boolean", operator: "=", value: true },
                { name: "refParent.id", type: "UUID", operator: "=", value: null }
            ],
            sort: [...sort, { name: "seq", type: "ASC" }]
        }
    })
        .then(page => (isSet ? (state.page = { ...page, where, sort }) : page))
        .finally(() => setIsLoading(false));
};

export const onClickSave = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callSave({ form: data.useForm.modelRef })
                .finally(() => setIsLoadingApp(false))
                .then(record => onClickView({ record, routerPrefix }));
        })
        .catch(error => validateError({ error }));
};

export const onClickSaveAll = ({ data }) => {
    setIsLoadingApp(true);
    return callSaveAll({ form: data })
        .finally(() => setIsLoadingApp(false))
        .then(() => onSearch({ ...state }));
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ["**"],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createColumns = () => {
    const columns = [
        createColumnInput({ title: () => i18n.global.t("model.country.nameTh"), dataIndex: "nameTh" }),
        createColumnInput({ title: () => i18n.global.t("model.country.nameEn"), dataIndex: "nameEn" }),
        createColumnInput({ title: () => i18n.global.t("model.country.nameEnS"), dataIndex: "nameEnS" }),
        createColumnInput({ title: () => i18n.global.t("model.country.alpha"), dataIndex: "alpha" }),
        createColumnInput({ title: () => i18n.global.t("model.country.url"), dataIndex: "url" }),
        createColumnAttachment({ title: () => i18n.global.t("model.country.refAttach"), dataIndex: "refAttach" })
    ];
    return columns;
};

export const createForm = () => {
    return {
        seq: null,
        nameTh: null,
        nameEn: null,
        nameEnS: null,
        url: null,
        refAttach: null,
        isSpecial: null,
        refParent: null,
        haveChildren: null,
        children: []
    };
};

export const createRules = () => {
    return {
        nameTh: [required]
    };
};

export const createField = ({ props, data }) => {
    const viewOnly = props.viewOnly;
    const useForm = data.useForm;
    const { modelRef, initialModel } = useForm;
    return [
        createFieldSelect({
            key: "nameTh",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.country.modelName"),
            inputProp: () => {
                let disabled = viewOnly || initialModel.id;
                return {
                    value: modelRef,
                    "onUpdate:value": value => {
                        modelRef.children.forEach(d => {
                            d.isSpecial = false;
                            d.visibleItem = ({ item }) => item.isSpecial;
                        });
                        let children = [];
                        if (value) {
                            modelRef.children
                                .filter(d => value.children.map(d2 => d2.id).includes(d.id))
                                .forEach(d => {
                                    d.isSpecial = true;
                                    d.visibleItem = ({ item }) => item.isSpecial;
                                });

                            children = value.children
                                .filter(d => !modelRef.children.map(d2 => d2.id).includes(d.id))
                                .map(d => {
                                    d.isSpecial = true;
                                    d.visibleItem = ({ item }) => item.isSpecial;
                                    return d;
                                });
                            value.children = [];
                        }
                        lodash.merge(modelRef, { ...value, isSpecial: true });
                        modelRef.children.push(...children);
                    },
                    disabled,
                    list: ({ searchQuery }) => callCountry({ searchQuery, id: initialModel.id }),
                    optLabel: `nameEn`
                };
            },
            changeLog: { getValue: ({ record }) => record.nameEn }
        }),
        createFieldInput({
            key: "url",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.country.url"),
            inputProp: () => ({ value: modelRef.url, "onUpdate:value": value => (modelRef.url = value), disabled: viewOnly })
        }),
        createFieldAttachment({
            key: "refAttach",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.country.refAttach"),
            inputProp: () => ({ value: modelRef.refAttach, "onUpdate:value": value => (modelRef.refAttach = value), disabled: viewOnly })
        })
    ];
};

export const createFormTitle = ({ data }) => {
    return data.useForm.initialModel.id ? data.useForm.initialModel.nameTh : i18n.global.t("button.create");
};

export const loadForm = ({ props, data }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) });
    } else {
        promise = Promise.resolve({ data: [modelRef] });
    }
    return promise.then(page => initUseForm({ data, page, routerPrefix })).finally(() => setIsLoading(false));
};

export const initState = ({ props }) => {
    const menuContext = props.menuContext;
    state.manages = [
        createManageSelect({
            menuContext,
            getCheckboxProps: ({ record }) => ({ disabled: record.isActive !== true || window.selectGetCheckboxPropDisabled?.({ record }) })
        }),
        /*
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) => onClickActiveAll({ rowSelection, callActiveAll, state, onSearch, setIsLoading })
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) => onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch, setIsLoading })
        }),
        */
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickSaveAll({
                    data: lodash.cloneDeep(rowSelection.selectedRows).map(d => {
                        d.isSpecial = false;
                        return d;
                    })
                })
        }),
        createManageExportCsv({
            menuContext,
            callData: ({ page }) => onSearch({ props, page: { ...lodash.cloneDeep({ ...page, page: 0, size: 1000000 }) }, isSet: false })
        })
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({ menuContext, onClick: ({ record }) => onClickEdit({ record, routerPrefix }) })
    ];
};

export const initForm = ({ props, data, refs }) => {
    checkAttribute({ props, data, refs });
    data.form = createForm();
    data.rules = createRules();
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly } = props;
    const { id, isActive } = data.useForm.initialModel;
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && has({ menuContext, key: "canEdit" })) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive))) {
        col1.push(createButtonSave({ onClick: () => onClickSave({ data }), classStyle: "m-1" }));
    }
    /*
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    */
    if (!viewOnly && id && isActive && has({ menuContext, key: "canDelete" })) {
        col2.push(
            createButtonDelete({
                onClick: () =>
                    onClickDelete({
                        record: { id },
                        callDelete: () => {
                            data.useForm.modelRef.isSpecial = false;
                            return callSave({ form: data.useForm.modelRef });
                        },
                        setIsLoading: setIsLoadingApp,
                        state,
                        routerPrefix
                    }),
                classStyle: "m-1"
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const openModalList = ({ bind, onIframeMounted }) => {
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleIFrame), bind),
        render: () => (
            <CIFrame
                src={`${process.env.VUE_APP_PUBLIC_PATH}/app/master-mgt/country-requests?isLayout=false&canSelect=true&cacheQuery=true`}
                onIframeMounted={({ refs }) => onIframeMounted({ options, refs })}
            ></CIFrame>
        )
    });
    return options;
};

export const state = reactive({
    columns: createColumns(),
    page: createPage({ options: { select: ["**"] } }),
    tableOptions: { search: { activeKey: "Search" } }
});
